'use client';

import { c as _c } from "react/compiler-runtime";
import { Chip } from '@module/mdrt-org/shared/components/chip/chip';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { useRouter } from '@module/mdrt-org/shared/i18n/i18n-navigation';
import { deleteProfilePicture } from '@module/mdrt-org/shared/utils/data/delete-profile-picture';
import { useInformationChipProps } from '@module/mdrt-org/shared/utils/data/get-chips-data/use-information-chip-props';
import { type InformationChipCMSContent } from '@module/mdrt-org/shared/utils/data/get-chips-data/utils/helpers';
import { postUpgradeMembershipApplication } from '@module/mdrt-org/shared/utils/data/post-upgrade-membership-application';
import { useProfilePictureUploadMutation } from '@module/mdrt-org/shared/utils/hooks/use-profile-picture-upload-mutation';
import { type ApplicationDefinition } from '@module/mdrt-org/shared/utils/types/application-definition';
import { type CustomerProfile } from '@module/mdrt-org/shared/utils/types/customer-profile';
import { type MembershipApplicationDto } from '@module/mdrt-org/shared/utils/types/membership-application';
import { ProfilePhotoContainer, type ProfilePhotoContainerProps } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/components/profile-photo-container/profile-photo-container';
import { ProfileSummaryActions } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/components/profile-summary-actions/profile-summary-actions';
import { ProfileSummary, type ProfileSummaryProps } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/components/profile-summary/profile-summary';
import { ProfileDashboardHeroSection } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/profile-dashboard-hero-section';
import { GTM_ACTION_CLICK, GTM_EVENT_UPLOAD_PHOTO, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { HeroSectionCompanyAdmin } from './hero-section-company-admin/hero-section-company-admin';
import { HeroSectionContentWrapper } from './hero-section-content-wrapper/hero-section-content-wrapper';
import { type CompanyAdminSectionContent } from './hero-section-wrapper-server';
type HeroSectionWrapperProps = {
  companyAdminSectionCMSContent: CompanyAdminSectionContent;
  informationChipData: {
    applicationDefinition: ApplicationDefinition;
    cmsContent: InformationChipCMSContent;
    customerProfile: CustomerProfile;
    membershipApplication: MembershipApplicationDto | null;
  };
  membershipYear: number | undefined;
  personId: number | undefined;
  profilePhotoContainerPartialProps: Omit<ProfilePhotoContainerProps, 'abortOngoingRequests' | 'dialogComponent' | 'handleProfilePictureDelete' | 'handleSubmitProfilePicture'>;
  profileSummaryProps: ProfileSummaryProps;
  showCompanyAdminSection: boolean;
};
export const HeroSectionWrapper = t0 => {
  const $ = _c(32);
  const {
    companyAdminSectionCMSContent,
    informationChipData,
    membershipYear,
    personId,
    profilePhotoContainerPartialProps,
    profileSummaryProps,
    showCompanyAdminSection
  } = t0;
  const router = useRouter();
  const {
    cancel: abortOngoingRequests,
    mutate: uploadProfilePicture
  } = useProfilePictureUploadMutation();
  const membershipApplicationUrl = informationChipData.cmsContent.membershipPageLink;
  let t1;
  if ($[0] !== uploadProfilePicture) {
    t1 = async formData => {
      if (formData instanceof FormData) {
        const file = formData?.get("file");
        if (file && file instanceof File) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_UPLOAD_PHOTO,
            label: file?.name || ""
          });
        }
      }
      return await uploadProfilePicture(formData);
    };
    $[0] = uploadProfilePicture;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSubmitProfilePicture = t1;
  let t2;
  if ($[2] !== membershipApplicationUrl || $[3] !== membershipYear || $[4] !== personId || $[5] !== router) {
    t2 = async () => {
      await postUpgradeMembershipApplication({
        membershipYear,
        personId
      });
      router.push(membershipApplicationUrl);
    };
    $[2] = membershipApplicationUrl;
    $[3] = membershipYear;
    $[4] = personId;
    $[5] = router;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== informationChipData || $[8] !== membershipApplicationUrl || $[9] !== t2) {
    t3 = {
      ...informationChipData,
      handleUpgradeMembershipClick: t2,
      membershipApplicationUrl
    };
    $[7] = informationChipData;
    $[8] = membershipApplicationUrl;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const informationChipProps = useInformationChipProps(t3);
  let t4;
  if ($[11] !== companyAdminSectionCMSContent || $[12] !== showCompanyAdminSection) {
    t4 = <HeroSectionCompanyAdmin cmsContent={companyAdminSectionCMSContent} showCompanyAdminSection={showCompanyAdminSection} />;
    $[11] = companyAdminSectionCMSContent;
    $[12] = showCompanyAdminSection;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== personId) {
    t5 = async () => await deleteProfilePicture({
      personId
    });
    $[14] = personId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== abortOngoingRequests || $[17] !== handleSubmitProfilePicture || $[18] !== profilePhotoContainerPartialProps || $[19] !== t5) {
    t6 = <ProfilePhotoContainer {...profilePhotoContainerPartialProps} abortOngoingRequests={abortOngoingRequests} dialogComponent={Dialog} handleProfilePictureDelete={t5} handleSubmitProfilePicture={handleSubmitProfilePicture} />;
    $[16] = abortOngoingRequests;
    $[17] = handleSubmitProfilePicture;
    $[18] = profilePhotoContainerPartialProps;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== informationChipProps) {
    t7 = informationChipProps?.label && <Chip {...informationChipProps} dataTestId="chip-information-button" isSmall={true} />;
    $[21] = informationChipProps;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t7) {
    t8 = <ProfileSummaryActions>{t7}</ProfileSummaryActions>;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== profileSummaryProps) {
    t9 = <ProfileSummary {...profileSummaryProps} />;
    $[25] = profileSummaryProps;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t4 || $[28] !== t6 || $[29] !== t8 || $[30] !== t9) {
    t10 = <HeroSectionContentWrapper><ProfileDashboardHeroSection companyAdminSlot={t4} profilePhotoSlot={t6} profileSummaryActionsSlot={t8} profileSummarySlot={t9} /></HeroSectionContentWrapper>;
    $[27] = t4;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};